import { BayLevelEnum } from "open-vessel-definition";
import type { IBayCgsInfo } from "./getContainersCgs";
import type { IBayLevelOffsetBottomBase } from "open-vessel-definition";
import type { IBayPattern } from "../../types/IPositionPatterns";
import { VCG_SEPARATION_TO_DECK } from "../../consts";
import { assertNonNullish } from "@tedivo/tedivo-pure-helpers";

/** Get the Most observed in between levels vcg */
export function calculateGeneralMedianVcg(
  bayLevelVcgsInfo: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, IBayCgsInfo>,
  missingImportantVcgs: boolean,
  offsetBottomBases: Array<IBayLevelOffsetBottomBase>,
): {
  generalMedianVcg: number;
  midVcgByBay: Record<IBayPattern, number>;
  midVcgByBayBlock: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, number>;
  offsetBottomBasesDict: Record<string, number>;
} {
  const midVcgByBayBlock: Record<IBayPattern | `${IBayPattern}-${IBayPattern}`, number> = {};
  const midVcgByBay: Record<IBayPattern, number> = {};
  const stats: Map<number, number> = new Map();

  const offsetBottomBasesDict = missingImportantVcgs
    ? offsetBottomBases.reduce((acc, bb) => {
        acc[`${bb.bays.join("-")}-${bb.level}`] = bb.offset;
        return acc;
      }, {} as Record<string, number>)
    : ({} as Record<string, number>);

  const everyBayBlockIds = Object.keys(bayLevelVcgsInfo).sort() as (
    | IBayPattern
    | `${IBayPattern}-${IBayPattern}`
  )[];

  everyBayBlockIds.forEach((bayBlockId) => {
    const bayBlock = bayLevelVcgsInfo[bayBlockId];
    const bayVcgsAbove = bayBlock[BayLevelEnum.ABOVE];
    const bayVcgsBelow = bayBlock[BayLevelEnum.BELOW];

    if (bayVcgsAbove === undefined && bayVcgsBelow === undefined) return;

    const adjAboveBB = offsetBottomBasesDict[`${bayBlockId}-${BayLevelEnum.ABOVE}`] || 0;
    const adjBelowBB = offsetBottomBasesDict[`${bayBlockId}-${BayLevelEnum.BELOW}`] || 0;

    const calcAlg =
      bayVcgsAbove !== undefined && bayVcgsBelow !== undefined
        ? CALC_ALG.ABOVE_AND_BELOW
        : bayVcgsAbove !== undefined
        ? CALC_ALG.ONLY_ABOVE
        : CALC_ALG.ONLY_BELOW;

    let midVcgOfThisBay = 0;

    midVcgOfThisBay = calculateMidVcgOfThisBay(
      calcAlg,
      bayVcgsAbove,
      bayVcgsBelow,
      adjAboveBB,
      adjBelowBB,
    );

    // Save the midVcg for the bay block
    midVcgByBayBlock[bayBlockId] = midVcgOfThisBay;

    bayBlockId.split("-").forEach((bay) => {
      midVcgByBay[bay as IBayPattern] = midVcgOfThisBay;
    });

    if (midVcgOfThisBay !== Infinity) {
      const statsOfValue = stats.get(midVcgOfThisBay) || 0;
      stats.set(midVcgOfThisBay, statsOfValue + 1);
    }
  });

  const generalMedianVcg = Array.from(stats.keys()).sort((a, b) => b - a)[0] || 0;

  return {
    midVcgByBay,
    midVcgByBayBlock,
    generalMedianVcg,
    offsetBottomBasesDict,
  };
}

enum CALC_ALG {
  ONLY_ABOVE = "ONLY_ABOVE",
  ONLY_BELOW = "ONLY_BELOW",
  ABOVE_AND_BELOW = "ABOVE_AND_BELOW",
}

function calculateMidVcgOfThisBay(
  calcAlg: CALC_ALG,
  bayVcgsAbove: IBayLevelCgsInfo | undefined,
  bayVcgsBelow: IBayLevelCgsInfo | undefined,
  adjAboveBB: number,
  adjBelowBB: number,
): number {
  switch (calcAlg) {
    case CALC_ALG.ONLY_ABOVE:
      assertNonNullish(bayVcgsAbove, "bayVcgsAbove is required");
      return bayVcgsAbove.bottomBase + adjAboveBB - VCG_SEPARATION_TO_DECK;

    case CALC_ALG.ONLY_BELOW:
      assertNonNullish(bayVcgsBelow, "bayVcgsBelow is required");
      return (
        bayVcgsBelow.maxHeightWithSeparationAbsoluteOfBay + adjBelowBB + VCG_SEPARATION_TO_DECK
      );

    case CALC_ALG.ABOVE_AND_BELOW:
      assertNonNullish(bayVcgsAbove, "bayVcgsAbove is required");
      assertNonNullish(bayVcgsBelow, "bayVcgsBelow is required");
      {
        const aboveBottomBase = bayVcgsAbove.bottomBase + adjAboveBB;

        const belowMaxHeightWithSeparation =
          adjBelowBB + bayVcgsBelow.maxHeightWithSeparationAbsoluteOfBay;

        return (aboveBottomBase + belowMaxHeightWithSeparation) / 2;
      }
  }
}

type IBayLevelCgsInfo = IBayCgsInfo[BayLevelEnum.ABOVE] | IBayCgsInfo[BayLevelEnum.BELOW];
